// Stripe Service
// Handles calls to Firebase Functions for Stripe payment operations

import { auth } from '../firebase/config';
import { firebaseConfig as appFirebaseConfig, apiConfig } from '../config/app';

const getAuthHeaders = async () => {
  const user = auth.currentUser;
  if (!user) {
    return {};
  }
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
};

/**
 * Get the Firebase Functions URL for a given function name
 */
const getFunctionUrl = (functionName) => appFirebaseConfig.getFunctionUrl(functionName);

/**
 * Create Stripe Checkout Session
 * @param {string} planType - 'lite' or 'premium'
 * @param {string} userId - Firebase Auth UID
 * @param {string} userEmail - User email (optional)
 * @returns {Promise<Object>} Checkout session with URL
 */
export const createCheckoutSession = async (planType, userId, userEmail = null) => {
  if (!planType || !userId) {
    throw new Error('planType and userId are required');
  }

  if (!['lite', 'premium'].includes(planType)) {
    throw new Error('Invalid planType. Must be "lite" or "premium"');
  }

  try {
    const functionUrl = getFunctionUrl('createCheckoutSession');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        planType,
        userId,
        userEmail,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    console.error('Create Checkout Session Error:', error);
    throw error;
  }
};

/**
 * Create Stripe Customer Portal Session
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<Object>} Portal session with URL
 */
export const createPortalSession = async (userId) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const functionUrl = getFunctionUrl('createPortalSession');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        userId,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    console.error('Create Portal Session Error:', error);
    throw error;
  }
};

/**
 * Get Subscription Status from Stripe
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<Object>} Subscription status
 */
export const getSubscriptionStatus = async (userId) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const functionUrl = getFunctionUrl('getSubscriptionStatus');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(`${functionUrl}?userId=${encodeURIComponent(userId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    console.error('Get Subscription Status Error:', error);
    throw error;
  }
};

/**
 * Verify Stripe Checkout Session
 * @param {string} sessionId - Stripe checkout session ID
 * @returns {Promise<Object>} Verified session details
 */
export const verifyCheckoutSession = async (sessionId) => {
  if (!sessionId) {
    throw new Error('sessionId is required');
  }

  try {
    const functionUrl = getFunctionUrl('verifyCheckoutSession');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(`${functionUrl}?sessionId=${encodeURIComponent(sessionId)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    console.error('Verify Checkout Session Error:', error);
    throw error;
  }
};

/**
 * Create Trial Subscription
 * @param {string} userId - Firebase Auth UID
 * @param {string} userEmail - User email (optional)
 * @returns {Promise<Object>} Trial subscription details
 */
export const createTrialSubscription = async (userId, userEmail = null) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const functionUrl = getFunctionUrl('createTrialSubscription');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        userId,
        userEmail,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    const msg = (error?.message || '').toLowerCase();
    if (!msg.includes('stripe_secret_key') && !msg.includes('is required')) {
      console.error('Create Trial Subscription Error:', error);
    }
    throw error;
  }
};

/**
 * Migrate LocalStorage Trial to Stripe
 * @param {string} userId - Firebase Auth UID
 * @param {string} userEmail - User email (optional)
 * @param {string} trialEndsAt - Trial end date from localStorage (ISO string)
 * @returns {Promise<Object>} Migration result
 */
export const migrateTrialToStripe = async (userId, userEmail = null, trialEndsAt = null) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const functionUrl = getFunctionUrl('migrateLocalStorageTrial');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        userId,
        userEmail,
        trialEndsAt,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    console.error('Migrate Trial Error:', error);
    throw error;
  }
};

/**
 * Validate Vehicle Creation
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<Object>} Validation result
 */
export const validateVehicleCreation = async (userId) => {
  if (!userId) {
    throw new Error('userId is required');
  }

  try {
    const functionUrl = getFunctionUrl('validateVehicleCreation');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        userId,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    console.error('Validate Vehicle Creation Error:', error);
    throw error;
  }
};

/**
 * Modify Stripe Subscription (Upgrade/Downgrade)
 * @param {string} userId - Firebase Auth UID
 * @param {string} newPriceId - New Stripe Price ID
 * @param {boolean} changeImmediately - Whether to change immediately or at period end
 * @returns {Promise<Object>} Updated subscription details
 */
export const modifySubscription = async (userId, newPriceId, changeImmediately = true) => {
  if (!userId || !newPriceId) {
    throw new Error('userId and newPriceId are required');
  }

  try {
    const functionUrl = getFunctionUrl('modifySubscription');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), apiConfig.timeout.default);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(await getAuthHeaders()),
      },
      body: JSON.stringify({
        userId,
        newPriceId,
        changeImmediately,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout. Please try again.');
    }
    console.error('Modify Subscription Error:', error);
    throw error;
  }
};

